// R-131-FIN-DECISION-A — nuclear SMR pathway preset (NREL ATB FOAK).

import type { ProjectIntent } from '../../../types/siteRag';
import type { SiteContext } from '../../../types/siteContext';
import type { Pathway } from '../types';
import {
  DEFAULT_BASE_WACC_PCT,
  annualMwhFromCf,
  buildCapexBlock,
  buildStandardBtmPhases,
  capexFromPerKw,
  computeAnnualOpexUsd,
  mergePathway,
  pathwayIdFromTechnology,
  resolveFacilityLoadMw,
  resolveNameplateMw,
  resolveProjectStartDate,
  valueOfServedLoadRevenue,
  type PathwayPresetOptions,
} from './shared';

/** NREL ATB 2024 SMR FOAK + EPRI benchmarks. */
export const NUCLEAR_SMR_BENCHMARKS = {
  capexPerKwUsd: 6500,
  fixedOmPerKwYearUsd: 100,
  variableOmPerMwhUsd: 2,
  fuelCostPerMwhUsd: 8,
  designLifeYears: 40,
  availabilityPct: 90,
  capacityFactorPct: 90,
  constructionMonths: 84,
  riskPremiumBps: 200,
  refurbYearFromCod: 20,
  refurbPctOfCapex: 0.25,
  decommPctOfCapex: 0.15,
  contingencyPct: 15,
} as const;

export function presetFor(
  intent: ProjectIntent,
  _siteContext: SiteContext,
  options?: PathwayPresetOptions,
): Pathway {
  // Generation NAMEPLATE: facility LOAD grossed up to a plant that SERVES the
  // firm 24/7 load (nuclear ≈ load × 1.24). Capex / energy / O&M ride this; the
  // load is carried separately for load-tracking costs.
  const facilityLoadMw = resolveFacilityLoadMw(intent);
  const capacityMw = resolveNameplateMw(intent, 'nuclear-smr');
  const startDate = resolveProjectStartDate(intent);
  const b = NUCLEAR_SMR_BENCHMARKS;

  const baseCapexUsd = capexFromPerKw(capacityMw, b.capexPerKwUsd);
  const decommissioningUsd = baseCapexUsd * b.decommPctOfCapex;
  const refurbUsd = baseCapexUsd * b.refurbPctOfCapex;
  const annualMwh = annualMwhFromCf(capacityMw, b.capacityFactorPct);
  const annualOpexUsd = computeAnnualOpexUsd(
    capacityMw,
    b.fixedOmPerKwYearUsd,
    b.variableOmPerMwhUsd,
    b.fuelCostPerMwhUsd,
    annualMwh,
  );

  const capex = buildCapexBlock(startDate, b.constructionMonths, baseCapexUsd, {
    contingencyPct: b.contingencyPct,
  });

  const codYear =
    new Date(startDate).getUTCFullYear() + Math.ceil(b.constructionMonths / 12);

  const base: Pathway = {
    id: pathwayIdFromTechnology('nuclear-smr'),
    name: 'BTM Nuclear SMR (FOAK)',
    classification: 'btm',
    technology: 'nuclear-smr',
    capacityMW: capacityMw,
    facilityLoadMW: facilityLoadMw,
    phases: buildStandardBtmPhases({
      startDate,
      constructionMonths: b.constructionMonths,
      designLifeYears: b.designLifeYears,
      totalCapexUsd: baseCapexUsd,
      decommissioningUsd,
      annualMwh,
      annualOpexUsd,
    }),
    capex,
    opex: {
      fixedOmPerKwYearUsd: b.fixedOmPerKwYearUsd,
      variableOmPerMwhUsd: b.variableOmPerMwhUsd,
      fuelCostPerMwhUsd: b.fuelCostPerMwhUsd,
      refurbishmentEvents: [
        {
          yearFromCod: b.refurbYearFromCod,
          usd: refurbUsd,
          description: 'Mid-life steam generator / reactor vessel refurbishment (25% of CAPEX)',
        },
      ],
    },
    // VALUE-CONSISTENT credit — same value-of-served-load as every other
    // pathway (incl. grid). See valueOfServedLoadRevenue in shared.ts.
    revenue: valueOfServedLoadRevenue({
      termYears: b.designLifeYears,
      availabilityPct: b.availabilityPct,
    }),
    risk: {
      technologyMaturity: 'first-of-kind',
      regulatoryRisk: 'high',
      fuelPriceRisk: 'none',
      baseWaccPct: DEFAULT_BASE_WACC_PCT,
      riskPremiumBps: b.riskPremiumBps,
    },
    carbon: {
      tCo2ePerMwh: 0,
    },
    lifetime: {
      designLifeYears: b.designLifeYears,
      decommissioningUsd,
      decommissioningYear: codYear + b.designLifeYears,
    },
  };

  return mergePathway(base, options?.overrides);
}
